import BaseList, {
  ListProps as BaseListProps,
  TListEntry,
} from '../../../../../volto-list-component/src/BaseList';

import PropTypes from 'prop-types';
import collapseSVG from '../../../icons/list_collapse.svg';
// @ts-ignore
import { Icon } from '@plone/volto/components';
import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';

interface ListProps<Item extends TListEntry> extends BaseListProps<Item> {
  defaultCollapsed?: boolean;
  headColor?: string;
  headTitle: string;
}

function deepCloneWithFunctions(obj: any): any {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map(deepCloneWithFunctions);
  }
  const clone: any = {};
  for (const key in obj) {
    if (typeof obj[key] === 'function') {
      clone[key] = obj[key];
    } else {
      clone[key] = deepCloneWithFunctions(obj[key]);
    }
  }
  return clone;
}

function List<Item extends TListEntry>({
  defaultCollapsed,
  headColor,
  headTitle,
  itemColumns,
  type,
  sortDir = 'desc',
  ...props
}: ListProps<Item>) {
  const injectTitle = useCallback(
    (
      title: string,
      columns: ListProps<Item>['itemColumns'],
    ): ListProps<Item>['itemColumns'] => {
      const firstColumnKey = Object.keys(columns)[0];
      const column = columns[firstColumnKey];
      const preLabel: string = JSON.stringify(column.preLabel);

      column.preLabel = (
        <div style={{ display: 'inline-block' }}>
          <span style={{ backgroundColor: headColor }} className="table-title">
            {title}
          </span>
          {preLabel}
        </div>
      );

      return columns;
    },
    [headColor],
  );

  const [collapsed, setCollapsed] = useState<boolean>(
    defaultCollapsed ?? false,
  );
  const [columns, setColumns] = useState<ListProps<Item>['itemColumns']>(
    injectTitle(headTitle, deepCloneWithFunctions(itemColumns)),
  );
  useEffect(() => {
    setColumns(injectTitle(headTitle, deepCloneWithFunctions(itemColumns)));
  }, [headTitle, itemColumns, injectTitle]);

  return (
    <div className={classNames('list-component', `type-${type}`)}>
      <div role="complementary">
        <Icon
          className={classNames('collapse', { collapsed })}
          color={headColor}
          size="1.3em"
          onClick={() => setCollapsed((i) => !i)}
          data-collapsed={collapsed}
          name={collapseSVG}
        />
      </div>
      <BaseList
        unstackable
        basic="very"
        itemColumns={columns}
        collapsed={collapsed}
        {...props}
        sortDir={sortDir}
      />
    </div>
  );
}

List.propTypes = {
  ...BaseList.propTypes,
  defaultCollapsed: PropTypes.bool,
  headTitle: PropTypes.string.isRequired,
  headColor: <Item extends TListEntry>(
    props: ListProps<Item>,
    propName: string,
    componentName: string,
  ) => {
    const color = props[propName];
    if (typeof color === 'undefined') {
      return null;
    }

    const s = new Option().style;
    s.color = color;
    if (s.color === '') {
      return new Error(
        `'${componentName}' field '${propName}' with value '${color}' is invalid.`,
      );
    }
    return null;
  },
};
List.defaultProps = {
  defaultCollapsed: false,
  headColor: 'grey',
};

export default List;
